import FileInput from "../../../common/FileInput";
import React, { SyntheticEvent, useCallback, useEffect, useState } from "react";
import Button from "../../../common/Button";
import { Board, pageFailure } from "../../../../reducers/uploadedBoard";
import {
  ButtonContainer,
  ColumnSelectors,
  FileInputContainer,
  FileInputs,
} from "./UploadBOMFileStyles";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import { useAppDispatch } from "../../../../utils/hooks/store";
import FlashPCBSelect, { OptionType } from "../../../common/FlashPCBSelect";
import {
  ColumnInfo,
  CSV_EXTENSION,
  EXCEL_EXTENSION,
  getFileColumns,
} from "../../../../utils/parseFile";
import { trackUploadBillOfMaterials } from "../../../../utils/observability/boardTracking";
import { UNIT_OPTIONS } from "../../../UploadBoard/GerberFileIdentifier";
import {
  getBOMColumnSuggestions,
  getPnPColumnSuggestions,
} from "../../../UploadBoard/GerberFileIdentifier/util/strategies/bestMatchFileSuggestions";
import Checkbox from "../../../common/Checkbox";
import LineItem from "../../../common/LineItem";
import FlashPCBTooltip from "../../../common/FlashPCBTooltip";
import InfoIcon from "../../../common/FlashPCBTooltip/InfoIcon";
import LoadingOverlay from "../../../LoadingOverlay";
import {
  useGetAlertMessage,
  useSendUpdateEvent,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/sendUpdates";
import {
  useCustomAlert,
  useCustomConfirm,
} from "../../../../utils/hooks/alerts";
import {
  BoardUploadFileType,
  UploadFilesResponse,
  useUploadBoardFiles,
} from "../../../../utils/hooks/uploadToS3";
import {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import { useBillOfMaterials } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";

enum FileTypes {
  BOM = "BOM",
  PNP = "PNP",
}

export default function UploadBOMFiles({
  onUpload,
}: {
  onUpload?: () => void;
}) {
  const billOfMaterials = useBillOfMaterials();
  const customConfirm = useCustomConfirm();
  const customAlert = useCustomAlert();
  const dispatch = useAppDispatch();
  const boardStuff = useBoardFromContext();
  const board = boardStuff.board as Board;
  const [bomFiles, setBomFiles] = useState<File[]>([]);
  const [bomFileColumnInfo, setBomFileColumnInfo] = useState<ColumnInfo | null>(
    null,
  );
  const [refColumn, setRefColumn] = useState<string>();
  const [mpnColumn, setMPNColumn] = useState<string>();
  const [manufacturerColumn, setManufacturerColumn] = useState<string>();
  const [footprintColumn, setFootprintColumn] = useState<string>();
  const [valueTextColumn, setValueTextColumn] = useState<string>();
  const [pnpFiles, setPnpFiles] = useState<File[]>([]);
  const [pnpFileColumnInfo, setPnpFileColumnInfo] = useState<ColumnInfo | null>(
    null,
  );
  const [pnpRefColumn, setPNPRefColumn] = useState<string>();
  const [pnpXColumn, setPNPXColumn] = useState<string>();
  const [pnpYColumn, setPNPYColumn] = useState<string>();
  const [pnpUnit, setPNPUnit] = useState<string>();
  const [pnpRotColumn, setPNPRotColumn] = useState<string>();
  const [pnpMountColumn, setPNPMountColumn] = useState<string>();
  const [overwriteParts, setOverwriteParts] = useState<boolean>(true);
  const [overwritePlacements, setOverwritePlacements] = useState<boolean>(true);
  const errorHandler = () => {
    dispatch(pageFailure({ boardId: board.id }));
  };
  const { sendUpdateEvent, loading: sendingBoardEvent } = useSendUpdateEvent();
  const { uploadBoardFiles, sending } = useUploadBoardFiles(
    errorHandler,
    BoardUploadFileType.AUXILIARY,
  );
  const getAlertMessage = useGetAlertMessage();

  const uploadFiles = () => {
    trackUploadBillOfMaterials(board);
    const saveBOMDetails = (fileLinks: UploadFilesResponse) => {
      const hasBomFile = bomFiles.length > 0;
      const pnpUploadDataIndex = hasBomFile ? 1 : 0;
      void sendUpdateEvent({
        machine_group: MachineGroup.BOARD,
        model_id: board.id,
        event: BoardTransitionEvent.SHOULD_UPDATE_BOM,
        update: {
          addition_bom_files: {
            bom_file_info: hasBomFile
              ? {
                  location: {
                    s3_key: fileLinks.upload_data[0]!.s3_key,
                    s3_bucket: fileLinks.upload_data[0]!.s3_bucket,
                  },
                  file_name: bomFiles[0]!.name,
                  header_row_index: (
                    bomFileColumnInfo as ColumnInfo
                  ).headerRowIndex.toString(),
                  designator_column: refColumn as string,
                  mpn_column: mpnColumn as string,
                  manufacturer_column: manufacturerColumn as string,
                  // TODO: fix
                  footprint_column: footprintColumn as string,
                  value_text_column: valueTextColumn as string,
                }
              : null,
            pnp_file_info:
              pnpFiles.length > 0
                ? {
                    location: {
                      s3_key: fileLinks.upload_data[pnpUploadDataIndex]!.s3_key,
                      s3_bucket:
                        fileLinks.upload_data[pnpUploadDataIndex]!.s3_bucket,
                    },
                    file_name: pnpFiles[0]!.name,
                    header_row_index: (
                      pnpFileColumnInfo as ColumnInfo
                    ).headerRowIndex.toString(),
                    designator_column: pnpRefColumn as string,
                    x_column: pnpXColumn as string,
                    y_column: pnpYColumn as string,
                    unit: pnpUnit as string,
                    rotation_column: pnpRotColumn as string,
                    mount_type_column: pnpMountColumn as string,
                  }
                : null,
            overwrite_part_selections: overwriteParts,
            overwrite_placements: overwritePlacements,
          },
        },
      }).then(onUpload);
    };
    const files = [...bomFiles, ...pnpFiles];
    uploadBoardFiles(board.id, files, saveBOMDetails);
  };

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const confirmationMessage = getAlertMessage(board);
    if (confirmationMessage !== null) {
      customConfirm(confirmationMessage, uploadFiles);
    } else {
      uploadFiles();
    }
  };

  const readFileColumns = useCallback(
    async (file: File, fileType: FileTypes) => {
      const columnInfo = await getFileColumns(file.name, file, customAlert);
      if (fileType === FileTypes.BOM) {
        setBomFileColumnInfo(columnInfo);
      } else if (fileType === FileTypes.PNP) {
        setPnpFileColumnInfo(columnInfo);
      }
    },
    [customAlert],
  );

  useEffect(() => {
    const suggestedColumns =
      pnpFileColumnInfo !== null
        ? getPnPColumnSuggestions(pnpFileColumnInfo.headers)
        : null;
    if (suggestedColumns !== null) {
      if (suggestedColumns.y_column) {
        setPNPYColumn(suggestedColumns.y_column);
      }
      if (suggestedColumns.x_column) {
        setPNPXColumn(suggestedColumns.x_column);
      }
      if (suggestedColumns.designator_column) {
        setPNPRefColumn(suggestedColumns.designator_column);
      }
      if (suggestedColumns.rotation_column) {
        setPNPRotColumn(suggestedColumns.rotation_column);
      }
      if (suggestedColumns.mount_type_column) {
        setPNPMountColumn(suggestedColumns.mount_type_column);
      }
      if (suggestedColumns.unit) {
        setPNPUnit(suggestedColumns.unit);
      }
    }
  }, [pnpFileColumnInfo]);

  useEffect(() => {
    const suggestedColumns =
      bomFileColumnInfo !== null
        ? getBOMColumnSuggestions(bomFileColumnInfo.headers)
        : null;
    if (suggestedColumns !== null) {
      if (suggestedColumns.designator_column) {
        setRefColumn(suggestedColumns.designator_column);
      }
      if (suggestedColumns.mpn_column) {
        setMPNColumn(suggestedColumns.mpn_column);
      }
      if (suggestedColumns.manufacturer_column) {
        setManufacturerColumn(suggestedColumns.manufacturer_column);
      }
      if (suggestedColumns.footprint_column) {
        setFootprintColumn(suggestedColumns.footprint_column);
      }
      if (suggestedColumns.value_text_column) {
        setValueTextColumn(suggestedColumns.value_text_column);
      }
    }
  }, [bomFileColumnInfo]);

  useEffect(() => {
    if (bomFiles.length > 0) {
      void readFileColumns(bomFiles[0]!, FileTypes.BOM);
    } else {
      setBomFileColumnInfo(null);
      setRefColumn(undefined);
      setMPNColumn(undefined);
      setManufacturerColumn(undefined);
      setFootprintColumn(undefined);
      setValueTextColumn(undefined);
    }
  }, [bomFiles, readFileColumns]);

  useEffect(() => {
    if (pnpFiles.length > 0) {
      void readFileColumns(pnpFiles[0]!, FileTypes.PNP);
    } else {
      setPnpFileColumnInfo(null);
      setPNPRefColumn(undefined);
      setPNPXColumn(undefined);
      setPNPYColumn(undefined);
      setPNPUnit(undefined);
      setPNPRotColumn(undefined);
      setPNPMountColumn(undefined);
    }
  }, [pnpFiles, readFileColumns]);

  const bomFileColumnOptions = bomFileColumnInfo?.headers.map((column) => ({
    value: column,
    label: column,
  }));

  const pnpFileColumnOptions = pnpFileColumnInfo?.headers.map((column) => ({
    value: column,
    label: column,
  }));

  const onChangeColumnSelection = (
    setter: (column: string | undefined) => void,
  ) => {
    return (newValue: OptionType | null) => {
      if (newValue !== null) {
        setter(newValue.value);
      } else {
        setter(undefined);
      }
    };
  };

  const isPNPColumnSelectionInvalid = () => {
    if (pnpFiles.length === 0) {
      return false;
    }
    return (
      pnpRefColumn === undefined ||
      pnpXColumn === undefined ||
      pnpYColumn === undefined ||
      pnpRotColumn === undefined ||
      pnpMountColumn === undefined ||
      pnpUnit === undefined
    );
  };

  const isBOMColumnSelectionInvalid = () => {
    if (bomFiles.length === 0) {
      return false;
    }
    return refColumn === undefined || mpnColumn === undefined;
  };

  const isFormDisabled = () => {
    return isPNPColumnSelectionInvalid() || isBOMColumnSelectionInvalid();
  };

  return (
    <>
      <LoadingOverlay
        loading={sendingBoardEvent || sending}
        text={"Uploading..."}
      />
      <div>
        <FileInputs>
          <FileInputContainer>
            <FileInput
              fileExtensions={[EXCEL_EXTENSION, CSV_EXTENSION]}
              files={bomFiles}
              setFiles={setBomFiles}
              inOrderToText={"to upload a bill of materials"}
            />
            {bomFiles.length > 0 && (
              <ColumnSelectors>
                <FlashPCBSelect
                  name="Reference designator column"
                  label="Reference designator column (Required)"
                  options={bomFileColumnOptions}
                  onChange={onChangeColumnSelection(setRefColumn)}
                  isClearable={true}
                  value={
                    refColumn === undefined
                      ? undefined
                      : {
                          value: refColumn,
                          label: refColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="Manufacturer's part number column"
                  label="Manufacturer's part number column (Required)"
                  options={bomFileColumnOptions}
                  onChange={onChangeColumnSelection(setMPNColumn)}
                  isClearable={true}
                  value={
                    mpnColumn === undefined
                      ? undefined
                      : {
                          value: mpnColumn,
                          label: mpnColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="Manufacturer column"
                  label="Manufacturer column"
                  options={bomFileColumnOptions}
                  onChange={onChangeColumnSelection(setManufacturerColumn)}
                  isClearable={true}
                  value={
                    manufacturerColumn === undefined
                      ? undefined
                      : {
                          value: manufacturerColumn,
                          label: manufacturerColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="Footprint column"
                  label="Footprint column"
                  options={bomFileColumnOptions}
                  onChange={onChangeColumnSelection(setFootprintColumn)}
                  isClearable={true}
                  value={
                    footprintColumn === undefined
                      ? undefined
                      : {
                          value: footprintColumn,
                          label: footprintColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="Value text column"
                  label="Value text column"
                  options={bomFileColumnOptions}
                  onChange={onChangeColumnSelection(setValueTextColumn)}
                  isClearable={true}
                  value={
                    valueTextColumn === undefined
                      ? undefined
                      : {
                          value: valueTextColumn,
                          label: valueTextColumn,
                        }
                  }
                />
              </ColumnSelectors>
            )}
          </FileInputContainer>
          <FileInputContainer>
            <FileInput
              fileExtensions={[EXCEL_EXTENSION, CSV_EXTENSION]}
              files={pnpFiles}
              setFiles={setPnpFiles}
              inOrderToText={"to upload placement data"}
            />
            {pnpFiles.length > 0 && (
              <ColumnSelectors>
                <FlashPCBSelect
                  name="Reference designator column"
                  label="Reference designator column (Required)"
                  options={pnpFileColumnOptions}
                  onChange={onChangeColumnSelection(setPNPRefColumn)}
                  isClearable={true}
                  value={
                    pnpRefColumn === undefined
                      ? undefined
                      : {
                          value: pnpRefColumn,
                          label: pnpRefColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="X position column"
                  label="X position column (Required)"
                  options={pnpFileColumnOptions}
                  onChange={onChangeColumnSelection(setPNPXColumn)}
                  isClearable={true}
                  value={
                    pnpXColumn === undefined
                      ? undefined
                      : {
                          value: pnpXColumn,
                          label: pnpXColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="Y position column"
                  label="Y position column (Required)"
                  options={pnpFileColumnOptions}
                  onChange={onChangeColumnSelection(setPNPYColumn)}
                  isClearable={true}
                  value={
                    pnpYColumn === undefined
                      ? undefined
                      : {
                          value: pnpYColumn,
                          label: pnpYColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="X, Y unit"
                  label="X, Y unit (Required)"
                  options={UNIT_OPTIONS}
                  onChange={onChangeColumnSelection(setPNPUnit)}
                  isClearable={true}
                  value={UNIT_OPTIONS.find(({ value }) => value == pnpUnit)}
                />
                <FlashPCBSelect
                  name="Rotation column"
                  label="Rotation column (Required)"
                  options={pnpFileColumnOptions}
                  onChange={onChangeColumnSelection(setPNPRotColumn)}
                  isClearable={true}
                  value={
                    pnpRotColumn === undefined
                      ? undefined
                      : {
                          value: pnpRotColumn,
                          label: pnpRotColumn,
                        }
                  }
                />
                <FlashPCBSelect
                  name="Mout side column"
                  label="Mount side column (Required)"
                  options={pnpFileColumnOptions}
                  onChange={onChangeColumnSelection(setPNPMountColumn)}
                  isClearable={true}
                  value={
                    pnpMountColumn === undefined
                      ? undefined
                      : {
                          value: pnpMountColumn,
                          label: pnpMountColumn,
                        }
                  }
                />
              </ColumnSelectors>
            )}
          </FileInputContainer>
        </FileInputs>
      </div>
      {billOfMaterials && billOfMaterials.elements.length > 0 && (
        <ButtonContainer>
          {bomFiles.length > 0 && (
            <LineItem
              leftText={
                <>
                  Overwrite existing part selection
                  <FlashPCBTooltip content="If you leave this option unchecked, we will only update the part selection for elements that don't already have a part selected.">
                    <InfoIcon styleProps={{ marginLeft: "0.25rem" }} />
                  </FlashPCBTooltip>
                </>
              }
              rightText={
                <Checkbox
                  checked={overwriteParts}
                  onChange={() => setOverwriteParts(!overwriteParts)}
                />
              }
            />
          )}
          {pnpFiles.length > 0 && (
            <LineItem
              leftText={
                <>
                  Overwrite placement data
                  <FlashPCBTooltip content="If you leave this option unchecked, we will only update the placements for elements that don't already have placement data.">
                    <InfoIcon styleProps={{ marginLeft: "0.25rem" }} />
                  </FlashPCBTooltip>
                </>
              }
              rightText={
                <Checkbox
                  checked={overwritePlacements}
                  onChange={() => setOverwritePlacements(!overwritePlacements)}
                />
              }
            />
          )}
        </ButtonContainer>
      )}
      <ButtonContainer>
        <Button
          label={"Upload files"}
          onClick={onSubmit}
          disabled={isFormDisabled()}
        />
      </ButtonContainer>
    </>
  );
}
